import { productTaxonomyCopyCatalog, productTaxonomyTermCatalog } from './renames';
import type {
  ProductTaxonomyCopyEntry,
  ProductTaxonomyCopyTermReference,
  ProductTaxonomyStage,
  ProductTaxonomyTermEntry,
  ProductTaxonomyTermForms,
  ProductTaxonomyVariant,
} from './types';

export type { ProductTaxonomyStage, ProductTaxonomyVariant } from './types';

export type ProductTaxonomyTermKey = keyof typeof productTaxonomyTermCatalog;
type CatalogTermForms = (typeof productTaxonomyTermCatalog)[ProductTaxonomyTermKey][ProductTaxonomyVariant];
export type ProductTaxonomyTermFormKey = keyof CatalogTermForms;
export type ProductTaxonomyTermReference = `${ProductTaxonomyTermKey & string}.${ProductTaxonomyTermFormKey & string}`;

export type ProductTaxonomyCopyKey = keyof typeof productTaxonomyCopyCatalog;

function resolveTermForms(termKey: ProductTaxonomyTermKey, stage: ProductTaxonomyStage): ProductTaxonomyTermForms {
  // The `as const` catalog narrows entries to their literal shapes, which drops the
  // optional `final` override; read through the declared entry type to recover it.
  const entry: ProductTaxonomyTermEntry = productTaxonomyTermCatalog[termKey];

  if (stage === 'final') {
    return entry.final ?? entry.future;
  }

  return entry[stage];
}

export function resolveProductTaxonomyTerm(
  reference: ProductTaxonomyTermReference,
  stage: ProductTaxonomyStage,
): string {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const [termKey, formKey] = reference.split('.') as [ProductTaxonomyTermKey, ProductTaxonomyTermFormKey];
  return resolveTermForms(termKey, stage)[formKey];
}

export function resolveProductTaxonomyCopy(key: ProductTaxonomyCopyKey, stage: ProductTaxonomyStage): string {
  // The `as const` catalog narrows entries to their literal shapes, which drops the
  // optional `final` override; read through the declared entry type to recover it.
  const entry: ProductTaxonomyCopyEntry = productTaxonomyCopyCatalog[key];

  if ('term' in entry) {
    return resolveProductTaxonomyCopyTerm(entry.term, stage);
  }

  if (stage === 'final') {
    return entry.final ?? entry.future;
  }

  return entry[stage];
}

function resolveProductTaxonomyCopyTerm(
  reference: ProductTaxonomyCopyTermReference,
  stage: ProductTaxonomyStage,
): string {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const [termKey, formKey] = reference.split('.') as [string, ProductTaxonomyTermFormKey];

  if (!(termKey in productTaxonomyTermCatalog)) {
    throw new Error(`Unknown product taxonomy term reference: ${reference}`);
  }

  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const value = resolveTermForms(termKey as ProductTaxonomyTermKey, stage)[formKey];

  if (value == null) {
    throw new Error(`Unknown product taxonomy term reference: ${reference}`);
  }

  return value;
}
